/**
 * Daily Stoic Quote - Floating Widget
 *
 * A draggable, always-visible floating widget that displays daily Stoic wisdom.
 * Like a margin note in a book - a grounding reminder that floats in the corner.
 *
 * Features:
 * - 15% screen width (min 200px, max 280px)
 * - Draggable to any position
 * - Remembers position in localStorage
 * - Minimizable to small icon
 * - Semi-transparent when idle
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { motion, useDragControls, AnimatePresence } from 'framer-motion';
import { getDailyStoicQuote, getRandomStoicQuote, StoicQuote } from '@/constants/stoicQuotes';
import { useIsMobile } from '@/hooks/useIsMobile';
import styles from './DailyStoicQuote.module.css';

interface DailyStoicQuoteProps {
  /** The date to show the quote for (defaults to today) */
  date?: Date;
}

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY = 'dailyStoicPosition';
const MINIMIZED_KEY = 'dailyStoicMinimized';

// Get initial position (bottom-right corner)
function getDefaultPosition(): Position {
  return {
    x: window.innerWidth - 300,
    y: window.innerHeight - 280,
  };
}

// Load saved position from localStorage
function loadPosition(): Position | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const pos = JSON.parse(saved);
      // Validate position is still on screen
      if (
        pos.x >= 0 &&
        pos.x < window.innerWidth - 100 &&
        pos.y >= 0 &&
        pos.y < window.innerHeight - 100
      ) {
        return pos;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

// Load minimized state (default to minimized on mobile)
function loadMinimized(isMobileViewport: boolean): boolean {
  // On mobile, always start minimized
  if (isMobileViewport) return true;
  try {
    return localStorage.getItem(MINIMIZED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function DailyStoicQuote({ date = new Date() }: DailyStoicQuoteProps) {
  const isMobile = useIsMobile();
  const [position, setPosition] = useState<Position>(getDefaultPosition);
  const [isMinimized, setIsMinimized] = useState(() => loadMinimized(window.innerWidth < 768));
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showNewQuote, setShowNewQuote] = useState(false);
  const constraintsRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();

  // Get the daily quote based on the date
  const dailyQuote = useMemo(() => getDailyStoicQuote(date), [date]);
  const [randomQuote, setRandomQuote] = useState<StoicQuote | null>(null);
  const currentQuote = randomQuote || dailyQuote;

  // Load saved position on mount
  useEffect(() => {
    const savedPos = loadPosition();
    if (savedPos) {
      setPosition(savedPos);
    }
  }, []);

  // Save position when it changes
  const handleDragEnd = (_: any, info: { point: Position }) => {
    setIsDragging(false);
    const newPos = { x: info.point.x - 140, y: info.point.y - 20 };
    setPosition(newPos);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPos));
    } catch {
      // Ignore storage errors
    }
  };

  // Toggle minimized state
  const toggleMinimized = () => {
    const newState = !isMinimized;
    setIsMinimized(newState);
    try {
      localStorage.setItem(MINIMIZED_KEY, String(newState));
    } catch {
      // Ignore storage errors
    }
  };

  const handleNewQuote = () => {
    setShowNewQuote(true);
    setRandomQuote(getRandomStoicQuote());
    setTimeout(() => setShowNewQuote(false), 300);
  };

  const handleResetToDaily = () => {
    setShowNewQuote(true);
    setRandomQuote(null);
    setTimeout(() => setShowNewQuote(false), 300);
  };

  // Format date
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      {/* Invisible constraints container */}
      <div ref={constraintsRef} className={styles.constraints} />

      {/* Floating widget */}
      <motion.div
        className={`${styles.floatingContainer} ${isMinimized ? styles.minimized : ''} ${isDragging ? styles.dragging : ''}`}
        style={{
          x: position.x,
          y: position.y,
        }}
        drag
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0.1}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        animate={{
          opacity: isDragging ? 1 : isHovered ? 1 : 0.85,
          scale: isDragging ? 1.02 : 1,
        }}
        transition={{ duration: 0.2 }}
      >
        <AnimatePresence mode="wait">
          {isMinimized ? (
            /* Minimized state - just an icon */
            <motion.button
              key="minimized"
              className={styles.minimizedButton}
              onClick={toggleMinimized}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title="Expand Daily Stoic"
              aria-label="Expand Daily Stoic quote"
            >
              📜
            </motion.button>
          ) : (
            /* Expanded state - full widget */
            <motion.div
              key="expanded"
              className={styles.widget}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              {/* Drag handle / Header */}
              <div className={styles.dragHandle} onPointerDown={(e) => dragControls.start(e)}>
                <span className={styles.dragIcon}>⋮⋮</span>
                <span className={styles.label}>DAILY STOIC</span>
                <span className={styles.date}>{formattedDate}</span>
                <button
                  className={styles.minimizeButton}
                  onClick={toggleMinimized}
                  title="Minimize"
                  aria-label="Minimize quote widget"
                >
                  −
                </button>
              </div>

              {/* Quote content */}
              <div className={styles.content}>
                <AnimatePresence mode="wait">
                  <motion.blockquote
                    key={showNewQuote ? 'new' : 'current'}
                    className={styles.quote}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className={styles.quoteText}>"{currentQuote.text}"</p>
                    <footer className={styles.quoteFooter}>
                      <cite className={styles.author}>— {currentQuote.author}</cite>
                      {currentQuote.source && (
                        <span className={styles.source}>{currentQuote.source}</span>
                      )}
                    </footer>
                  </motion.blockquote>
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className={styles.footer}>
                {currentQuote.theme && (
                  <span className={styles.theme}>{currentQuote.theme.replace('-', ' ')}</span>
                )}
                <div className={styles.actions}>
                  <button
                    className={styles.actionButton}
                    onClick={handleNewQuote}
                    title="Get a different quote"
                    aria-label="Get a different quote"
                  >
                    ↻
                  </button>
                  {randomQuote && (
                    <button
                      className={styles.actionButton}
                      onClick={handleResetToDaily}
                      title="Return to today's quote"
                      aria-label="Return to today's quote"
                    >
                      ◉
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
